// Side-effect import: patches Pixi's renderer/UBO/shader systems with
// polyfills that avoid `new Function(...)` / `eval`. Required because our
// production CSP (vercel.json) does not allow `unsafe-eval`. Without this
// import Application.init() throws "Current environment does not allow
// unsafe-eval" before the canvas is mounted, leaving GameScreen blank
// after the player taps ASCEND. Must be imported before `Application` is
// instantiated; ESM import-hoisting makes the placement here safe.
import "pixi.js/unsafe-eval";
import { Application } from "pixi.js";
import { CLIP_CAPTURE_PRESERVE_DRAWING_BUFFER } from '../sharing/clips/flag';
import { computePlayfieldRect, type PlayfieldRect } from './hud-anchors';

/**
 * Check if WebGL is available in the current environment.
 */
function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    return gl !== null;
  } catch {
    return false;
  }
}

export interface RendererHandle {
  app: Application;
  dispose: () => void;
}

/**
 * Initialize the PixiJS application with context loss handling
 * and visibility-based render optimization.
 *
 * Call `dispose()` before `app.destroy()` to remove the document/canvas
 * event listeners — otherwise `webglcontextlost` (fired during destroy)
 * and later `visibilitychange` events try to call `app.stop()` on a
 * torn-down ticker.
 *
 * T-4.3: passes the container to Pixi's built-in `resizeTo` so any change
 * in the container's dimensions (window drag, fullscreen toggle, monitor
 * swap) automatically calls `renderer.resize()`. The companion
 * `attachResizeObserver` helper bridges container size changes into the
 * caller-provided HUD-relayout callback.
 */
export async function setupRenderer(
  container: HTMLElement,
): Promise<RendererHandle> {
  if (!isWebGLAvailable()) {
    throw new Error(
      "WebGL is not supported in this browser. Please use a modern browser to play Rogue's Cog.",
    );
  }

  const app = new Application();
  const width = container.clientWidth || 375;
  const height = container.clientHeight || 667;
  await app.init({
    width,
    height,
    background: 0x1a1510, // dark soot — steampunk lower floors
    // Transparent outside the board: the canvas spans the full window and
    // sits above the LetterboxFrame, so the brass side bars must show
    // through the margins the Rogue scampers into. GameRenderer paints an
    // opaque soot backing under the board itself.
    backgroundAlpha: 0,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio, 2),
    preference: 'webgl',
    preserveDrawingBuffer: CLIP_CAPTURE_PRESERVE_DRAWING_BUFFER, // false by default; see FEATURE_PLAN.md §4.2
    // T-4.3: bind Pixi's auto-resize to the container element. Pixi listens
    // to `window.resize` internally and reads `container.clientWidth/Height`
    // on each tick to call `renderer.resize()`. The ResizeObserver we attach
    // separately fires on container-only changes (sidebar collapse, parent
    // flex reflow) which `window.resize` would miss.
    resizeTo: container,
  });

  container.appendChild(app.canvas as HTMLCanvasElement);

  const controller = new AbortController();
  const { signal } = controller;
  const canvas = app.canvas as HTMLCanvasElement;

  canvas.addEventListener(
    "webglcontextlost",
    (event: Event) => {
      event.preventDefault();
      if (!app.ticker) return;
      console.warn("WebGL context lost. Attempting recovery...");
      app.stop();
    },
    { signal },
  );

  canvas.addEventListener(
    "webglcontextrestored",
    () => {
      if (!app.ticker) return;
      console.info("WebGL context restored.");
      app.start();
    },
    { signal },
  );

  const isCinematic =
    typeof window !== 'undefined' &&
    window.location.search.includes('cinematic=1');

  if (!isCinematic) {
    document.addEventListener(
      "visibilitychange",
      () => {
        if (!app.ticker) return;
        if (document.hidden) {
          app.stop();
        } else {
          app.start();
        }
      },
      { signal },
    );
  } else {
    (window as unknown as { __rcPixiApp?: unknown }).__rcPixiApp = app;
  }

  return { app, dispose: () => controller.abort() };
}

/**
 * Callback signature for HUD relayout after the canvas resizes.
 *
 * `playfield` is the inner rect (canvas minus letterbox bars) that the
 * 9:16 game board occupies after uniform scaling. Consumers anchor HUD
 * elements off this rect via `computeAnchorPosition` from `hud-anchors.ts`.
 */
export type RendererResizeCallback = (
  width: number,
  height: number,
  playfield: PlayfieldRect,
) => void;

/**
 * Attach a `ResizeObserver` to the canvas container and a Pixi `'resize'`
 * listener to the renderer. Both fire `onResize` with the new canvas size +
 * computed playfield rect.
 *
 * Why both:
 *   - `ResizeObserver` fires on container changes that `window.resize` misses
 *     (CSS-driven reflow, parent flex changes).
 *   - Pixi's `'resize'` event fires after the renderer has actually re-sampled
 *     to the new size, so HUD pixel positions land against the post-resize
 *     canvas. Calling our callback from both sources is idempotent — the
 *     callback's only side effect is updating React state with the new rect,
 *     which is debounced by React itself.
 *
 * Returns a `dispose` function that disconnects both observers.
 *
 * Defensive guards:
 *   - If `ResizeObserver` is undefined (very old browsers / SSR), the helper
 *     gracefully degrades to the Pixi `'resize'` listener only.
 *   - The first `onResize` is called synchronously with the current container
 *     size, so consumers don't need to seed React state from
 *     `getViewportSize()` separately.
 */
export function attachResizeObserver(
  app: Application,
  container: HTMLElement,
  onResize: RendererResizeCallback,
): () => void {
  const fire = (w: number, h: number) => {
    const safeW = w > 0 && Number.isFinite(w) ? w : container.clientWidth || 0;
    const safeH = h > 0 && Number.isFinite(h) ? h : container.clientHeight || 0;
    const playfield = computePlayfieldRect(safeW, safeH);
    onResize(safeW, safeH, playfield);
  };

  // 1. Pixi renderer 'resize' event — fires after Pixi has applied the new
  //    backbuffer size. Provides authoritative post-resize dimensions.
  const onPixiResize = (w: number, h: number) => fire(w, h);
  app.renderer.on('resize', onPixiResize);

  // 2. ResizeObserver on the container element — fires on container reflows
  //    that don't trigger window.resize (parent flex/grid changes).
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const rect = entry.contentRect;
      fire(rect.width, rect.height);
    });
    ro.observe(container);
  }

  // 3. Initial fire so the consumer has a layout before the first observer
  //    callback. Container size is read synchronously.
  fire(container.clientWidth, container.clientHeight);

  return () => {
    app.renderer.off('resize', onPixiResize);
    if (ro) {
      ro.disconnect();
      ro = null;
    }
  };
}
